/**
 * React component for the Hotel Bookings section of the landing page
 *
 * Copyright (c) 2022 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useEffect, useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import moment from 'moment'
import { Row, Col } from 'reactstrap'
import shuffle from 'lodash/shuffle'
import SectionHeader from '../SectionHeader'
// import ExploreButton from '../ExploreButton'
import { COMPANY_ID, PlaceItemHotelCard } from 'client/app/js/components/TripAdvisor'
import LoadingScreen from 'client/shared/js/components/LoadingScreen'
import exploreApi from 'client/shared/js/services/explore/api'
import { logError } from 'client/shared/js/utils/browser'
import styles from './HotelBookings.module.scss'

const DATE_FORMAT = 'YYYY-MM-DD'

const HotelBookings = ({ className }) => {
  const [loading, setLoading] = useState(true)
  const [hotelLocations, setHotelLocations] = useState([])

  useEffect(() => {
    getPopularHotels()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getPopularHotels = useCallback(() => {
    const checkIn = moment().add(1, 'days').format(DATE_FORMAT)
    const checkOut = moment(checkIn).add(3, 'days').format(DATE_FORMAT)

    exploreApi.getPopularHotels(checkIn, checkOut)
      .then(({ data }) => setHotelLocations(data))
      .catch((err) => logError('Landing Page Popular Hotels Error', err))
      .finally(() => setLoading(false))
  }, [])

  const hotelResults = useMemo(() => {
    const firstPicks = []
    const secondPicks = []

    hotelLocations.forEach((hotel) => {
      if (hotel?.topHotels?.length > 0) {
        firstPicks.push(hotel.topHotels[0])
      }

      if (hotel?.topHotels?.length > 1) {
        secondPicks.push(hotel.topHotels[1])
      }
    })

    return {
      firstPicks: firstPicks.filter((hotel) => !!hotel),
      secondPicks: secondPicks.filter((hotel) => !!hotel)
    }
  }, [hotelLocations])

  const HotelCards = useMemo(() => {
    const { firstPicks, secondPicks } = hotelResults
    const hotels = shuffle(firstPicks).concat(shuffle(secondPicks))
    const firstSix = hotels.slice(0, 6)

    return firstSix.map((hotel, index) => (
      <Col
        key={`${COMPANY_ID}-${hotel?.locationId || index}`}
        sm='12'
        md='6'
        lg='6'
        xl='4'>
        <PlaceItemHotelCard
          className={cx(styles['hotel-bookings-card'])}
          size='lg'
          hotel={hotel} />
      </Col>
    ))
  }, [hotelResults])

  const title = hotelLocations.length > 0
    ? 'Some special offers are waiting'
    : 'Book to get ahead'

  const description = hotelLocations.length > 0
    ? 'Check out these exclusive deals for the next three days.'
    : 'Secure your accommodations first and plan accordingly.'

  if (!loading && hotelResults.length === 0) {
    return null
  }

  return (
    <section className={cx(styles['hotel-bookings-container'], className)}>
      <SectionHeader title={title} description={description} />
      <div className={cx(styles['hotel-cards-container'])}>
        <Row>
          {loading ? (
            <LoadingScreen className='pb-3' />
          ) : (
            HotelCards
          )}
        </Row>
      </div>
      {/* <div className={cx(styles['explore-btn-container'])}>
        <ExploreButton color='outline-muted' url={EXPLORE_URL} disableArrow />
      </div> */}
    </section>
  )
}

HotelBookings.propTypes = {
  className: PropTypes.string
}

export default HotelBookings
